import { useEffect, useRef, useState } from "react";
import type { Lesson } from "../domain/lesson-schema";
import type { Session } from "../domain/session-schema";

type Props = {
  readonly lesson: Lesson;
  readonly session: Session | null;
  readonly onCollapse: () => void;
  /** Opened by the learner: move focus to its close button so the keyboard lands inside it. */
  readonly focusOnOpen?: boolean;
};

function PhraseText({
  phrase,
  session,
}: {
  readonly phrase: Lesson["phrases"][number];
  readonly session: Session | null;
}) {
  const clamp = (offset: number) =>
    Math.max(0, Math.min(phrase.text.length, offset - phrase.startOffset));
  const heard = clamp(session?.committedCursor.textOffset ?? 0);
  const plan = session?.reentryPlan;
  const start = clamp(plan?.replayStartOffset ?? 0);
  const end = clamp(plan?.firstUnplayedOffset ?? 0);
  const boundaries = [...new Set([0, heard, start, end, phrase.text.length])].sort(
    (left, right) => left - right,
  );
  return boundaries.slice(1).map((until, index) => {
    const from = boundaries[index] ?? 0;
    const text = phrase.text.slice(from, until);
    return plan && from >= start && until <= end && start < end ? (
      <mark key={from} className="phrase-lead-in" title="이어서 듣기 시작 부분">
        {text}
      </mark>
    ) : (
      <span key={from} className={until <= heard ? "phrase-heard" : undefined}>
        {text}
      </span>
    );
  });
}

const phaseLabels = {
  completed: "들은 문장",
  current: "지금 문장",
  upcoming: "남은 문장",
} as const;

export function LessonPanel({ lesson, session, onCollapse, focusOnOpen = false }: Props) {
  const close = useRef<HTMLButtonElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the panel's opening moves focus.
  useEffect(() => {
    if (focusOnOpen) close.current?.focus();
  }, []);
  const [following, setFollowing] = useState(true);
  const rail = useRef<HTMLElement>(null);
  const current = session?.committedCursor.phraseIndex ?? 0;
  const resumeMarker = session?.reentryPlan
    ? `${session.turnId}:${session.reentryPlan.replayStartOffset}`
    : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: A new resume recenters even when the active phrase is unchanged.
  useEffect(() => {
    if (!following) return;
    const list = rail.current;
    const phrase = list?.querySelector<HTMLElement>(`[data-phrase-index="${current}"]`);
    if (list && phrase)
      list.scrollTop = Math.max(0, phrase.offsetTop - list.offsetTop - list.clientHeight / 3);
  }, [current, following, resumeMarker]);

  return (
    <aside className="panel script-panel" aria-labelledby="script-title" lang="ko">
      <div className="panel-heading">
        <h2 id="script-title">강의 스크립트</h2>
        <div className="script-tools">
          <label className="check-field">
            <input
              type="checkbox"
              checked={following}
              onChange={(event) => setFollowing(event.target.checked)}
            />
            위치 따라가기
          </label>
          <button ref={close} type="button" className="button quiet compact" onClick={onCollapse}>
            스크립트 닫기
          </button>
        </div>
      </div>
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: This bounded scroll region must support keyboard scrolling. */}
      <section className="phrase-list" ref={rail} aria-label="강의 문장" tabIndex={0}>
        <ol>
          {lesson.phrases.map((phrase) => {
            const plan = session?.reentryPlan;
            const restoring =
              plan &&
              phrase.endOffset > plan.replayStartOffset &&
              phrase.startOffset < plan.firstUnplayedOffset;
            const phase =
              phrase.index < current
                ? "completed"
                : phrase.index === current
                  ? "current"
                  : "upcoming";
            return (
              <li
                key={phrase.id}
                data-phrase-index={phrase.index}
                className={`phrase ${phase}`}
                aria-current={phase === "current" ? "step" : undefined}
              >
                <div className="phrase-label">
                  <span>{String(phrase.index + 1).padStart(2, "0")}</span>
                  <span className="visually-hidden">{phaseLabels[phase]}</span>
                  {restoring && <span>이어서 듣기 시작 부분</span>}
                </div>
                <p lang="ko">
                  <PhraseText phrase={phrase} session={session} />
                </p>
              </li>
            );
          })}
        </ol>
      </section>
    </aside>
  );
}
